/**
 * Head de-duplication guard.
 *
 * Hosting injects social/SEO metadata (canonical, description, og:*, twitter:*)
 * into the served index.html at request time. Those injected tags do NOT carry
 * the `data-rh` marker, so react-helmet-async cannot replace them — it appends
 * its own per-route tags instead, leaving two canonicals / two descriptions in
 * the rendered DOM.
 *
 * This guard removes the unmanaged duplicate once a Helmet-managed equivalent
 * exists, so every route renders exactly one self-referencing canonical and one
 * of each social tag. Static tags stay untouched until Helmet supplies a
 * replacement, so non-JS crawlers still see a complete head.
 */

const GROUPS = [
  "link[rel='canonical']",
  "meta[name='description']",
  "meta[name='robots']",
  "meta[property^='og:']",
  "meta[name^='twitter:']",
] as const;

function keyFor(el: Element): string {
  const name = el.getAttribute("name");
  const property = el.getAttribute("property");
  const rel = el.getAttribute("rel");
  return `${el.tagName}|${name ?? property ?? rel ?? ""}`;
}

function dedupe(): void {
  const head = document.head;
  if (!head) return;

  const managed = new Set<string>();
  const all: Element[] = [];

  for (const selector of GROUPS) {
    head.querySelectorAll(selector).forEach((el) => {
      all.push(el);
      if (el.hasAttribute("data-rh")) managed.add(keyFor(el));
    });
  }

  for (const el of all) {
    if (el.hasAttribute("data-rh")) continue;
    if (managed.has(keyFor(el))) el.remove();
  }
}

let started = false;

export function initHeadDedupe(): void {
  if (typeof document === "undefined" || started) return;
  started = true;

  let queued = false;
  const schedule = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      dedupe();
    });
  };

  schedule();
  new MutationObserver(schedule).observe(document.head, { childList: true });
}
